import type {
  Baf_CoaFragment,
  BunkerGrade,
  Currency,
  Baf_VoyageCharterFragment,
} from '../../../graphql';
import { BafTypes } from '../../../graphql';
import type { ScenarioOrder } from '../../../lib/planningData';
import { exhaustiveCheck, isDefined } from '../../../lib/util';
import type { BunkerPriceByGradeName } from '../../hooks/useBunkerPrices';
import { compact } from 'lodash';
import { convertToDefaultCurrency } from '../../../state/zustand';
import type { Income } from './income';
import type { TimeData } from '../../core/TimeData';
import { CoreEventType } from '../../core/TimeData';
import type { ClientId } from '../../core/ClientId';
import type {
  BunkerConsumption,
  BunkerConsumptionByPortCall,
} from '../costs/bunkerCost';
import type { LoadedAmount } from '../loadedAmounts';
import { DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import { getOrderCoaDetails } from '../getOderCoaDetails';
import { memoize } from '@seaber/common/lib/memoize';

type BafBase = {
  bunkerGrade: BunkerGrade;
  basePriceMin: number;
  basePriceMax: number;
  coaAssortmentId: number | null;
  coaRouteId: number | null;
  currency: Currency;
};

export type BafStepped = BafBase & {
  type: BafTypes.STEPPED;
  stepSize: number;
  stepEffect: number;
  startFromBase: boolean;
  useStepEffectAsValue: boolean;
};

export type BafActualBunkersUsed = BafBase & {
  type: BafTypes.ACTUAL_BUNKERS_USED;
};

export type BafAgreedBunkerAmount = BafBase & {
  type: BafTypes.AGREED_BUNKER_AMOUNT;
  agreedBunkerAmount: number;
};

export type Baf = BafStepped | BafActualBunkersUsed | BafAgreedBunkerAmount;

interface BafValueBase {
  amount: number;
  bunkerGrade: BunkerGrade;
  currency: Currency;
  priceDeviation: number;
}

interface BafSteppedValue extends BafValueBase {
  type: BafTypes.STEPPED;
  step: number;
}

interface BafBunkersUsedValue extends BafValueBase {
  type: BafTypes.ACTUAL_BUNKERS_USED;
  bunkerUsedTons: number;
}

interface BafAgreedBunkerAmountValue extends BafValueBase {
  type: BafTypes.AGREED_BUNKER_AMOUNT;
}

type BafValue =
  | BafBunkersUsedValue
  | BafAgreedBunkerAmountValue
  | BafSteppedValue;

function getPriceDeviation(
  minPrice: number,
  maxPrice: number,
  currentPrice: number,
): number {
  const basePrice =
    currentPrice < minPrice
      ? minPrice
      : currentPrice > maxPrice
        ? maxPrice
        : currentPrice;

  return currentPrice - basePrice;
}

export function calculateBafStepped(
  baf: BafStepped,
  bunkerPrice: number,
  totalFreight: number,
): BafSteppedValue {
  const priceDeviation = getPriceDeviation(
    baf.basePriceMin,
    baf.basePriceMax,
    bunkerPrice,
  );

  const step =
    Math.trunc(priceDeviation / baf.stepSize) + (baf.startFromBase ? 1 : 0);

  const effect = baf.useStepEffectAsValue
    ? baf.stepEffect
    : (baf.stepEffect / 100) * totalFreight;

  const amount = step * effect;

  return {
    amount,
    bunkerGrade: baf.bunkerGrade,
    currency: baf.currency,
    priceDeviation,
    step,
    type: BafTypes.STEPPED,
  };
}

export function calculateBafBunkersUsed(
  baf: BafActualBunkersUsed,
  bunkerPrice: number,
  bunkerUsedTons: number,
): BafBunkersUsedValue {
  const priceDeviation = getPriceDeviation(
    baf.basePriceMin,
    baf.basePriceMax,
    bunkerPrice,
  );

  const amount = priceDeviation * bunkerUsedTons;

  return {
    amount,
    bunkerGrade: baf.bunkerGrade,
    bunkerUsedTons,
    currency: baf.currency,
    priceDeviation,
    type: BafTypes.ACTUAL_BUNKERS_USED,
  };
}

export function calculateBafAgreedBunkerAmount(
  baf: BafAgreedBunkerAmount,
  bunkerPrice: number,
): BafAgreedBunkerAmountValue {
  const priceDeviation = getPriceDeviation(
    baf.basePriceMin,
    baf.basePriceMax,
    bunkerPrice,
  );

  const amount = priceDeviation * baf.agreedBunkerAmount;

  return {
    amount,
    bunkerGrade: baf.bunkerGrade,
    currency: baf.currency,
    priceDeviation,
    type: BafTypes.AGREED_BUNKER_AMOUNT,
  };
}

function getOrderContractBafs(
  coa: Baf_CoaFragment,
  coaAssortmentId: number,
  coaRouteId: number,
  defaultCurrency: Currency,
) {
  const contractCurrency = coa.currency ?? defaultCurrency;

  const allBafs: Baf[] = coa.coaContractBunkersByCoaContractId.nodes
    .map((coaBunker) => {
      if (!coaBunker || !coaBunker.bafType) return null;

      const {
        bafBunkerAmount,
        bafStartFromBase,
        bafStepEffect,
        bafStepSize,
        bafType,
        bafUseStepEffectAsValue,
        basePriceMax,
        bunkerGrade,
        coaAssortmentId,
        coaRouteId,
        price,
      } = coaBunker;

      const convertedPrice = convertToDefaultCurrency(+price, contractCurrency);

      /* If max price is not defined we consider minimum price also as the upper bound */
      const convertedBasePriceMax = basePriceMax
        ? convertToDefaultCurrency(+basePriceMax, contractCurrency)
        : convertedPrice;

      if (!convertedPrice || !convertedBasePriceMax) {
        return null;
      }

      const bafBase: BafBase = {
        basePriceMin: convertedPrice,
        basePriceMax: convertedBasePriceMax,
        bunkerGrade,
        coaAssortmentId: coaAssortmentId ?? null,
        coaRouteId: coaRouteId ?? null,
        currency: coa.currency ?? defaultCurrency,
      };

      switch (bafType) {
        case BafTypes.STEPPED: {
          if (!bafStepSize || !bafStepEffect) {
            return null;
          }

          const stepEffect = bafUseStepEffectAsValue
            ? convertToDefaultCurrency(+bafStepEffect, contractCurrency)
            : +bafStepEffect;

          if (!stepEffect) {
            return null;
          }

          const baf: BafStepped = {
            ...bafBase,
            type: bafType,
            stepEffect,
            stepSize: +bafStepSize,
            startFromBase: bafStartFromBase ?? false,
            useStepEffectAsValue: bafUseStepEffectAsValue ?? false,
          };

          return baf;
        }
        case BafTypes.ACTUAL_BUNKERS_USED: {
          const baf: BafActualBunkersUsed = {
            ...bafBase,
            type: bafType,
          };

          return baf;
        }
        case BafTypes.AGREED_BUNKER_AMOUNT: {
          if (!bafBunkerAmount) {
            return null;
          }

          const baf: BafAgreedBunkerAmount = {
            ...bafBase,
            type: bafType,
            agreedBunkerAmount: +bafBunkerAmount,
          };

          return baf;
        }
        default: {
          exhaustiveCheck(bafType);
        }
      }
    })
    .filter(isDefined);

  const bafsByBunkerGrade = mapGroupBy(
    allBafs,
    (coaBunker) => coaBunker.bunkerGrade,
  );

  const bafs: Baf[] = [];

  // For each bunker grade find the best matching contract
  bafsByBunkerGrade.forEach((groupedBafs) => {
    let matchingBaf: Baf | null = null;
    let matchingByAssortment = false;
    let matchingByRoute = false;

    groupedBafs.forEach((baf) => {
      // Already found the best matching alternative so we can return early
      if (matchingByAssortment && matchingByRoute) return;

      const hasAssortmentReq = !!baf.coaAssortmentId;
      const assortmentMatches =
        hasAssortmentReq && baf.coaAssortmentId === coaAssortmentId;
      const hasRouteReq = !!baf.coaRouteId;
      const routeMatches = hasRouteReq && baf.coaRouteId === coaRouteId;

      if (assortmentMatches && routeMatches) {
        matchingByAssortment = true;
        matchingByRoute = true;
        matchingBaf = baf;
      }

      if (assortmentMatches) {
        matchingByAssortment = true;
        matchingBaf = baf;
      }

      // Do not consider worse alternatives if already found contract bunker
      // with matching assortment
      if (matchingByAssortment) return;

      if (routeMatches) {
        matchingByRoute = true;
        matchingBaf = baf;
      }

      // No need to consider worse alternatives
      if (matchingByRoute) return;

      if (!hasAssortmentReq && !hasRouteReq) {
        matchingBaf = baf;
      }
    });

    if (matchingBaf !== null) {
      bafs.push(matchingBaf);
    }
  });

  return bafs;
}

export function getConsumptionByPortCall(tds: TimeData[]) {
  const consumptionByPortCall: BunkerConsumptionByPortCall = new Map();

  tds.forEach((td) => {
    const {
      laden,
      event: { portCallId },
    } = td;

    if (!portCallId) return;

    const consumptions: BunkerConsumption[] =
      consumptionByPortCall.get(portCallId) ?? [];
    const { insideEcaData, outsideEcaData, portData } = td.costs.bunkerGrade;

    if (insideEcaData) {
      consumptions.push({
        type: laden ? 'LADEN' : 'BALLAST',
        bunkerGrade: insideEcaData.referenceGrade,
        consumptionTonnes: insideEcaData.consumedBunker,
      });
    }

    if (outsideEcaData) {
      consumptions.push({
        type: laden ? 'LADEN' : 'BALLAST',
        bunkerGrade: outsideEcaData.referenceGrade,
        consumptionTonnes: outsideEcaData.consumedBunker,
      });
    }

    if (portData) {
      consumptions.push({
        type: 'PORT',
        bunkerGrade: portData.referenceGrade,
        consumptionTonnes: portData.consumedBunker,
      });
    }

    consumptionByPortCall.set(portCallId, consumptions);
  });

  return consumptionByPortCall;
}

export function getPortCallIdsByOrder(tds: TimeData[]) {
  const portCallIdsByOrder = new Map<number, Set<ClientId>>();
  const carriedOrderIds = new Set<number>();
  let prevPortCallId: ClientId | null = null;

  tds.forEach((td) => {
    const portCallId = td.event.portCallId;

    if (td.event.type === CoreEventType.Load) {
      const orderId = td.event.order.id;
      carriedOrderIds.add(orderId);

      if (portCallId) {
        portCallIdsByOrder.set(orderId, new Set([portCallId]));
      }
    }

    if (portCallId && portCallId !== prevPortCallId) {
      carriedOrderIds.forEach((orderId) => {
        portCallIdsByOrder.get(orderId)?.add(portCallId);
      });

      prevPortCallId = portCallId;
    }

    if (td.event.type === CoreEventType.Discharge) {
      carriedOrderIds.delete(td.event.order.id);
    }
  });

  return portCallIdsByOrder;
}

const getCoaRoutesByOrderIdByVcId = memoize(
  (orderById: Map<number, ScenarioOrder>) => {
    const allRels = [...orderById.values()].flatMap((o) =>
      compact([
        o.incomingVoyageCharterRelation,
        o.outgoingVoyageCharterRelation,
      ]),
    );

    const result: Map<number, Map<number, number | null>> = new DefaultMap(
      () => new Map(),
    );

    for (const rel of allRels) {
      result.get(rel.voyageCharterId)?.set(rel.orderId, rel.coaRouteId ?? null);
    }

    return result;
  },
);

export function getVoyageCharterBafIncomes(
  voyageCharter: Baf_VoyageCharterFragment,
  loadedAmounts: LoadedAmount[],
  orderById: Map<number, ScenarioOrder>,
  freightIncomeByOrderId: Map<number, number>,
  portCallIdsByOrder: Map<number, Set<ClientId>>,
  bunkerConsumptionByPortCall: BunkerConsumptionByPortCall,
  defaultCurrency: Currency,
  bunkerPriceByGradeName: BunkerPriceByGradeName,
) {
  const incomes: Income[] = [];
  const coa =
    voyageCharter.voyageCharterToContractsByVoyageCharterId.nodes.at(0)
      ?.coaContractByCoaId ?? null;

  if (!coa) return [];

  const coaData = {
    cargoUnitName: coa.cargoUnitByAmountUnitId?.name ?? null,
    freights: compact(coa.coaContractPricesByCoaContractId.nodes),
    coaAssortments: compact(
      coa.coaContractAssortmentsByCoaContractId.nodes,
    ).map((assortment) => ({
      ...assortment,
      assortmentId: assortment.assortmentId ?? null,
      assortmentGroupId: assortment.assortmentGroupId ?? null,
    })),
    routes: compact(coa.coaContractPortsByCoaContractId.nodes).map((route) => ({
      ...route,
      originId: route.originId ?? null,
      originZoneId: route.originZoneId ?? null,
      destinationId: route.destinationId ?? null,
      destinationZoneId: route.destinationZoneId ?? null,
      destinationZoneLocations: new Set(
        compact(route.zoneByDestinationZoneId?.zoneLocationsByZoneId.nodes).map(
          (zoneLocation) => zoneLocation.locationId,
        ),
      ),
      originZoneLocations: new Set(
        compact(route.zoneByOriginZoneId?.zoneLocationsByZoneId.nodes).map(
          (zoneLocation) => zoneLocation.locationId,
        ),
      ),
    })),
  };

  const coaRouteByOrderId = getCoaRoutesByOrderIdByVcId(orderById).get(
    voyageCharter.id,
  );
  if (!coaRouteByOrderId) {
    return [];
  }

  loadedAmounts.forEach((loadedAmount) => {
    const { orderId } = loadedAmount;
    const order = orderById.get(orderId);
    const coaRouteId = coaRouteByOrderId.get(orderId);
    const freightIncome = freightIncomeByOrderId.get(orderId);

    if (!order || !freightIncome) return;

    const { coaRoute, coaAssortment } = getOrderCoaDetails(
      {
        assortmentId: order.assortmentId ?? null,
        assortmentGroupId:
          order.assortmentByAssortmentId?.assortmentGroup ?? null,
        loadPortId: order.loadPortId ?? null,
        dischargePortId: order.dischargePortId ?? null,
      },
      { mass: loadedAmount.loadedMass, volume: loadedAmount.loadedVolume },
      coaData,
      coaRouteId,
    );

    if (!coaAssortment || !coaRoute) {
      return;
    }

    const orderContractBafs = getOrderContractBafs(
      coa,
      coaAssortment.id,
      coaRoute.id,
      defaultCurrency,
    );

    orderContractBafs.forEach((contractBaf) => {
      const bunkerPricePerTonne =
        bunkerPriceByGradeName(contractBaf.bunkerGrade) ?? 0;

      let baf: BafValue | null = null;

      switch (contractBaf.type) {
        case BafTypes.STEPPED: {
          baf = calculateBafStepped(
            contractBaf,
            bunkerPricePerTonne,
            freightIncome,
          );
          break;
        }
        case BafTypes.ACTUAL_BUNKERS_USED: {
          const portCallIds = Array.from(
            portCallIdsByOrder.get(order.id) ?? [],
          );
          const allConsumption = portCallIds.flatMap(
            (pcId) => bunkerConsumptionByPortCall.get(pcId) ?? [],
          );

          const bunkerUsed = allConsumption
            .filter(
              (c) =>
                c.type !== 'BALLAST' &&
                c.bunkerGrade === contractBaf.bunkerGrade,
            )
            .reduce((sum, c) => sum + c.consumptionTonnes, 0);

          baf = calculateBafBunkersUsed(
            contractBaf,
            bunkerPricePerTonne,
            bunkerUsed,
          );
          break;
        }
        case BafTypes.AGREED_BUNKER_AMOUNT: {
          baf = calculateBafAgreedBunkerAmount(
            contractBaf,
            bunkerPricePerTonne,
          );
          break;
        }
        default: {
          exhaustiveCheck(contractBaf);
        }
      }

      if (!baf) return;

      incomes.push({
        type: 'BAF',
        name: `Order #${order.id} BAF`,
        value: baf.amount,
        orderId: order.id,
        voyageCharterId:
          order.incomingVoyageCharterRelation?.voyageCharterId ?? null,
        params: getBafValueParams(baf),
      });
    });
  });

  return incomes;
}

function getBafValueParams(baf: BafValue): Array<{
  name: string;
  value: string | number | null;
}> {
  const sharedParams = [
    {
      name: `Bunker price difference (${baf.currency})`,
      value: baf.priceDeviation,
    },
    {
      name: 'Bunker grade',
      value: baf.bunkerGrade,
    },
  ];

  switch (baf.type) {
    case BafTypes.ACTUAL_BUNKERS_USED: {
      return [
        ...sharedParams,
        {
          name: 'Bunker used (t)',
          value: baf.bunkerUsedTons,
        },
      ];
    }
    case BafTypes.AGREED_BUNKER_AMOUNT: {
      return sharedParams;
    }
    case BafTypes.STEPPED: {
      return [
        ...sharedParams,
        {
          name: 'Step',
          value: baf.step,
        },
      ];
    }
    default: {
      exhaustiveCheck(baf);
    }
  }
}
